/**
 * The board's actionable middle: the three panels a reader is meant to look at
 * *before* the census — where callers are being refused, which quotas are nearly
 * gone, and which accounts nothing is watching.
 *
 * ## Why this section exists at all
 *
 * The KPI strip's first two tiles are the reason this board was built (a 33.74%
 * HTTP 429 rate on one account, a quota at 89.55% of a DataZoneStandard limit),
 * and §4.1 says an aggregate that leads nowhere is a defect. The routes those
 * tiles name — `/ai/capacity`, `/ai/estate` — have no page yet, so the panels
 * that explain those numbers live here, on the overview, anchored at
 * `THROTTLE_ANCHOR`, `QUOTA_ANCHOR` and `OBSERVABILITY_ANCHOR`. When the routes
 * ship, `aiHref` in `./params.ts` starts pointing the same tiles at them and
 * these panels move; nothing else changes.
 *
 * ## Altitude
 *
 * L1 throughout: aggregates plus a **worst-N**, never a full list. The estate is
 * 19 accounts and the folded quota payload is a few dozen rows, both of which
 * would still be an L2 table rendered too high (§1), so every panel here caps
 * its rows and says in its caption how many it is not showing.
 *
 * ## The two things this section must not get wrong
 *
 * - **`null` is not `0`.** Every figure renders through a `Reading` from
 *   `./status.ts` — never a bare number — so "the metric did not answer" and
 *   "the metric answered zero" cannot collapse into the same cell. A rate over
 *   zero calls is `undefined`, not `0%`.
 * - **The zeros in the coverage panel are bad news.** Zero alert rules and zero
 *   diagnostic settings are the measured estate-wide state, and they are toned
 *   `danger` with the words "nothing alerts" / "no logs leave" beside them —
 *   never green, never a tick (§8: a shortfall must not read as a healthy zero).
 *   `alertRulesReading` and `diagnosticsReading` already encode that; this file
 *   only renders their verdict.
 */

import { Badge, DataGrid, EmptyState, Meter, Typography } from "@norican/react";

import type { AiAccount, AiCapacitySummary, AiQuota } from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

import { PanelCard, ReadingCell, SectionHeading } from "./panel-card";
import { OBSERVABILITY_ANCHOR, QUOTA_ANCHOR, THROTTLE_ANCHOR } from "./params";
import {
  alertRulesReading,
  deploymentsReading,
  diagnosticsReading,
  formatCount,
  formatOfTotal,
  formatPercent,
  formatRatio,
  formatWindow,
  quotaReading,
  throttleReading,
  tokensReading,
} from "./status";

/** Quota rows shown as meters before the rest stay folded (§1). */
const WORST_QUOTAS = 5;
/** Accounts listed in the throttling panel. */
const WORST_THROTTLED = 8;
/** Accounts listed in the coverage panel. */
const WORST_UNWATCHED = 8;

interface AiAttentionSectionProps {
  /** Every discovered account — the per-account rates and coverage live here. */
  accounts: readonly AiAccount[];
  /** The quota rows the backend kept, already folded and worst-first. */
  quotas: readonly AiQuota[];
  /** Capacity headline numbers, and the thresholds every tone here is judged against. */
  capacity: AiCapacitySummary;
  /** The usage window as an ISO 8601 duration, stated on the panels it scopes. */
  window: string;
}

export function AiAttentionSection({
  accounts,
  quotas,
  capacity,
  window,
}: AiAttentionSectionProps) {
  const windowLabel = formatWindow(window);

  /* ── Throttling ────────────────────────────────────────────────────────
     Accounts nothing ever called have no rate at all, so they are not listed
     as clean: a rate over zero calls is undefined, not 0%. */
  const withTraffic = accounts.filter(
    (account) => account.throttle_pct != null || (account.calls_window ?? 0) > 0,
  );
  const rankedThrottled = [...withTraffic].sort(
    (a, b) => (b.throttle_pct ?? -1) - (a.throttle_pct ?? -1),
  );
  const shownThrottled = rankedThrottled.slice(0, WORST_THROTTLED);
  const anyThrottledHard = rankedThrottled.some(
    (account) => (account.throttle_pct ?? -1) >= capacity.throttle_warning_pct,
  );
  const anyThrottledSoft = rankedThrottled.some((account) => (account.throttle_pct ?? 0) > 0);
  const anyRateMeasured = rankedThrottled.some((account) => account.throttle_pct != null);
  const throttleTone: StatusTone = anyThrottledHard
    ? "danger"
    : anyThrottledSoft
      ? "warning"
      : anyRateMeasured
        ? "success"
        : "neutral";

  /* ── Quota ─────────────────────────────────────────────────────────────
     A ratio against a limit is a meter (§2), and only the worst few are drawn:
     the rest of the estate's rows sit at zero of their limit and the payload
     says so in `quota_fold_detail`. */
  const rankedQuotas = [...quotas].sort(
    (a, b) => (b.utilisation_pct ?? -1) - (a.utilisation_pct ?? -1),
  );
  const shownQuotas = rankedQuotas.slice(0, WORST_QUOTAS);
  const quotaTone: StatusTone =
    (capacity.quotas_exhausted ?? 0) > 0
      ? "danger"
      : (capacity.quotas_at_risk ?? 0) > 0
        ? "warning"
        : capacity.worst_utilisation_pct == null
          ? "neutral"
          : "success";

  /* ── Coverage ──────────────────────────────────────────────────────────
     Worst first, and "worst" here means *most missing*: an account with no
     alert rule and no diagnostic setting outranks an idle one. */
  const rankedUnwatched = [...accounts].sort(
    (a, b) => coverageGapRank(b) - coverageGapRank(a) || a.name.localeCompare(b.name),
  );
  const shownUnwatched = rankedUnwatched.slice(0, WORST_UNWATCHED);
  const unwatchedCount = accounts.filter(
    (account) => account.alert_rules === 0 || account.diagnostic_settings === 0,
  ).length;
  const coverageTone: StatusTone = unwatchedCount > 0 ? "danger" : "success";

  return (
    <section className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        title="Act on this first"
        subtitle="Every rate below is computed over the accounts whose metrics could actually be read, and the shortfall is stated rather than folded into the numerator. An em dash is a read that did not answer; a literal zero is a measurement — and in the coverage panel a zero is the finding, not the all-clear."
      />

      <div id={THROTTLE_ANCHOR} className="scroll-mt-6">
        <PanelCard
          title="Where callers are being refused"
          accentTone={throttleTone}
          caption={
            rankedThrottled.length === 0
              ? "No account served a request in the window"
              : `${formatOfTotal(shownThrottled.length, rankedThrottled.length)} accounts with traffic, worst first · ${windowLabel}` +
                (capacity.accounts_usage_unreadable > 0
                  ? ` · ${formatCount(capacity.accounts_usage_unreadable)} unreadable and excluded from every rate here`
                  : "")
          }
        >
          <div className="flex flex-col gap-3">
            {rankedThrottled.length === 0 ? (
              <EmptyState
                size="sm"
                title="No account served a request in this window"
                description="With no calls there is no 429 rate to report — a rate over zero calls is undefined, not 0%. Rendering a clean record for accounts nothing has ever called would be the flattering lie this board exists to avoid."
              />
            ) : (
              <DataGrid<AiAccount>
                data={shownThrottled}
                getRowId={(row) => row.id}
                density="compact"
                aria-label={`Accounts by HTTP 429 rate, worst first, over the ${windowLabel}`}
                columns={[
                  {
                    id: "account",
                    header: "Account",
                    accessor: (row) => row.name,
                    cell: (row) => (
                      <span className="flex flex-col">
                        <span className="text-fg text-sm font-medium">{row.name}</span>
                        <span className="text-fg-muted text-xs">
                          {row.subscription_name ?? row.subscription_id} · {row.location}
                        </span>
                      </span>
                    ),
                  },
                  {
                    id: "rate",
                    header: "429 rate",
                    align: "end",
                    accessor: (row) => row.throttle_pct,
                    cell: (row) => (
                      <ReadingCell
                        reading={throttleReading(
                          row.throttle_pct,
                          row.calls_window,
                          capacity.throttle_warning_pct,
                        )}
                        showBadge
                      />
                    ),
                  },
                  {
                    id: "refused",
                    header: "Refused / calls",
                    align: "end",
                    accessor: (row) => row.throttled_calls_window,
                    cell: (row) => (
                      <span className="tabular-nums">
                        {formatRatio(row.throttled_calls_window, row.calls_window)}
                      </span>
                    ),
                  },
                  {
                    id: "tokens",
                    header: "Tokens",
                    align: "end",
                    accessor: (row) => row.tokens_window,
                    cell: (row) => (
                      <ReadingCell reading={tokensReading(row.tokens_window, row.usage_state)} />
                    ),
                  },
                ]}
              />
            )}
            <Typography variant="body-sm" tone="muted">
              An account counts as throttled at or above{" "}
              {formatPercent(capacity.throttle_warning_pct)}.{" "}
              {capacity.worst_throttle_account == null
                ? "No account's throttling metric could be read, so the worst rate in the estate is unknown rather than zero."
                : `Worst in the estate: ${capacity.worst_throttle_account} at ${formatPercent(capacity.worst_throttle_pct)}.`}{" "}
              A 429 is a refused call — a caller retrying or failing, not a slow one.
            </Typography>
          </div>
        </PanelCard>
      </div>

      <div id={QUOTA_ANCHOR} className="scroll-mt-6">
        <PanelCard
          title="Quota headroom, worst first"
          accentTone={quotaTone}
          caption={
            capacity.quotas_total == null
              ? "The quota read failed — headroom is unknown, not unlimited"
              : `${formatOfTotal(shownQuotas.length, capacity.quotas_total)} quota rows · ${formatCount(capacity.quotas_exhausted)} exhausted, ${formatCount(capacity.quotas_at_risk)} at risk from ${formatPercent(capacity.warning_utilisation_pct)}`
          }
        >
          <div className="flex flex-col gap-4">
            {shownQuotas.length === 0 ? (
              <EmptyState
                size="sm"
                title="No quota row carried any consumption"
                description="The quota read answered and nothing in the estate is deployed against a limit. That is a measured result — a failed read would have rendered the access panel instead of this panel."
              />
            ) : (
              <ul className="flex flex-col gap-4">
                {shownQuotas.map((quota) => {
                  const reading = quotaReading(quota, capacity.warning_utilisation_pct);
                  return (
                    <li
                      key={`${quota.subscription_id}-${quota.quota_name}-${quota.location ?? "sub"}`}
                    >
                      {reading.unknown ? (
                        // No usable limit means no ratio to draw. A meter at zero
                        // would claim headroom that was never reported.
                        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <Typography variant="body-sm">{quota.quota_name}</Typography>
                          <ReadingCell reading={reading} showBadge />
                        </div>
                      ) : (
                        <Meter.Root
                          value={quota.utilisation_pct ?? 0}
                          min={0}
                          max={100}
                          size="sm"
                          tone={reading.tone === "neutral" ? "accent" : reading.tone}
                          aria-valuetext={`${quota.quota_name}: ${formatRatio(quota.used, quota.limit)} used, ${formatPercent(quota.utilisation_pct)} of the limit`}
                        >
                          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                            <Meter.Label>
                              <Typography variant="body-sm">{quota.quota_name}</Typography>
                            </Meter.Label>
                            <span className="flex items-center gap-2">
                              <span className="text-fg-muted text-xs tabular-nums">
                                {formatRatio(quota.used, quota.limit)}
                              </span>
                              <ReadingCell reading={reading} showBadge />
                            </span>
                          </div>
                          <Meter.Track>
                            <Meter.Indicator />
                          </Meter.Track>
                        </Meter.Root>
                      )}
                      <div className="text-fg-muted mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <span>{quota.subscription_name ?? quota.subscription_id}</span>
                        <Badge tone="neutral" variant="outline" size="xs">
                          {quota.scope === "subscription"
                            ? `subscription-wide · echoed by ${formatCount(quota.regions_reporting)} region(s)`
                            : (quota.location ?? "region unknown")}
                        </Badge>
                        {quota.deployments_backed > 0 && (
                          <Badge tone="neutral" variant="outline" size="xs">
                            backs {formatCount(quota.deployments_backed)} deployment(s)
                          </Badge>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <Typography variant="body-sm" tone="muted">
              {capacity.quota_fold_detail}
            </Typography>
            <Typography variant="body-sm" tone="muted">
              GlobalStandard and DataZoneStandard quota is subscription-wide but is echoed by every
              region endpoint the subscription touches, so the identical row arrives several times.
              The duplicates are folded out before this panel sees them — summing them would
              multiply the limit by the region count and render an exhausted quota as comfortable.
            </Typography>
          </div>
        </PanelCard>
      </div>

      <div id={OBSERVABILITY_ANCHOR} className="scroll-mt-6">
        <PanelCard
          title="What is idle, and what nothing is watching"
          accentTone={coverageTone}
          caption={
            accounts.length === 0
              ? "No account was discovered"
              : `${formatOfTotal(shownUnwatched.length, accounts.length)} accounts, most gaps first`
          }
        >
          <div className="flex flex-col gap-3">
            {shownUnwatched.length === 0 ? (
              <EmptyState
                size="sm"
                title="No AI account was discovered"
                description="There is nothing to report coverage on. The discovery query answered — a failed read would have rendered the access panel instead."
              />
            ) : (
              <DataGrid<AiAccount>
                data={shownUnwatched}
                getRowId={(row) => row.id}
                density="compact"
                aria-label="Accounts with their token usage, model deployments, alert rules and diagnostic settings, most coverage gaps first"
                columns={[
                  {
                    id: "account",
                    header: "Account",
                    accessor: (row) => row.name,
                    cell: (row) => (
                      <span className="flex flex-col">
                        <span className="text-fg text-sm font-medium">{row.name}</span>
                        <span className="text-fg-muted text-xs">
                          {row.kind} · {row.subscription_name ?? row.subscription_id}
                        </span>
                      </span>
                    ),
                  },
                  {
                    id: "tokens",
                    header: `Tokens (${windowLabel})`,
                    align: "end",
                    accessor: (row) => row.tokens_window,
                    cell: (row) => (
                      <ReadingCell reading={tokensReading(row.tokens_window, row.usage_state)} />
                    ),
                  },
                  {
                    id: "deployments",
                    header: "Deployments",
                    align: "end",
                    accessor: (row) => row.deployments,
                    cell: (row) => <ReadingCell reading={deploymentsReading(row.deployments)} />,
                  },
                  {
                    id: "alerts",
                    header: "Alert rules",
                    align: "end",
                    accessor: (row) => row.alert_rules,
                    cell: (row) => <ReadingCell reading={alertRulesReading(row.alert_rules)} />,
                  },
                  {
                    id: "diagnostics",
                    header: "Diagnostic settings",
                    align: "end",
                    accessor: (row) => row.diagnostic_settings,
                    cell: (row) => (
                      <ReadingCell reading={diagnosticsReading(row.diagnostic_settings)} />
                    ),
                  },
                ]}
              />
            )}
            <Typography variant="body-sm" tone="muted">
              {unwatchedCount > 0
                ? `${formatCount(unwatchedCount)} of ${formatCount(accounts.length)} account(s) have no alert rule or no diagnostic setting at all. Those zeros are the finding: nothing will page anyone when one of them starts refusing calls, and nothing can be investigated afterwards because no log leaves the account.`
                : "Every discovered account has at least one alert rule and one diagnostic setting. That is a measured result over the accounts that could be read."}{" "}
              An em dash in this table is a read that failed, and it is never counted as a gap —
              which is what keeps the gap count from being inflated as well as from being flattered.
            </Typography>
          </div>
        </PanelCard>
      </div>
    </section>
  );
}

/**
 * How much is *missing* on an account, as a sort weight.
 *
 * Deliberately not a health score and never rendered: it only decides which
 * rows a worst-N panel shows. Missing alerting and missing logging weigh most
 * because they are the two gaps nothing else on the board can compensate for; a
 * `null` scores nothing, because an unread field is not evidence of a gap.
 */
function coverageGapRank(account: AiAccount): number {
  let score = 0;
  if (account.alert_rules === 0) {
    score += 4;
  }
  if (account.diagnostic_settings === 0) {
    score += 4;
  }
  if (account.usage_state === "measured" && account.tokens_window === 0) {
    score += 2;
  }
  if (account.deployments === 0) {
    score += 1;
  }
  return score;
}
